import { ColorPalette } from "@/features/canvas/components/ColorPalette";
import { StrokeWidthSlider } from "@/features/canvas/components/StrokeWidthSlider";
import { ToolGrid } from "@/features/canvas/components/ToolGrid";
import type { SharedColor } from "@/lib/canvas/shared-shape";
import { TELESTRATOR_TOOL_KINDS } from "@/lib/canvas/telestrator-schema";
import {
  useReviewEditorStore,
  type EditorToolKind,
} from "@/stores/review-editor";

/**
 * Floating tool + composer overlay for Coach Review focus mode.
 *
 * Focus mode hides the canvas's own side toolbar (via the canvas's
 * `hideToolbar` prop) and lets the video/board fill the viewport.
 * The coach reveals the rail on demand by clicking the "Tools"
 * pill in the top-right corner. The revealed panel carries BOTH
 * the drawing tools AND the composer (`children`) — the same
 * stack as the inline right rail — so a coach in focus mode can
 * draw AND write the note without leaving focus mode. Mirrors the
 * legacy Replay focus-drawer (see
 * docs/archive/.../07-video-focus-drawer.png: tools at the top,
 * full composer below).
 *
 * Tool state source is the same `useReviewEditorStore` the canvas
 * reads from, so the overlay tools drive the canvas in real time.
 * The composer (`children`) is the SAME `composerElement` node the
 * inline rail renders — but it can only be mounted in one place at
 * a time, so CoachReview passes it here only while focus mode is
 * open (the inline canvas is unmounted then anyway).
 *
 * IMPORTANT — the panel is kept MOUNTED while focus mode is active
 * and only hidden via `hidden` when the "Tools" pill is collapsed.
 * It must NOT be conditionally unmounted: `children` is a live
 * React Hook Form instance, and unmounting it on "Close tools"
 * would destroy the coach's in-progress composer edits (the parent
 * snapshots RHF state on focus CLOSE, not on panel collapse, so an
 * unmount here is silent data loss). The drawing tools are
 * store-backed and stateless, so they ride along harmlessly.
 */

const TACTICAL_TOOLS: readonly EditorToolKind[] = [
  "select",
  "arrow",
  "line",
  "zone",
  "label",
  "freehand",
  "player",
  "ball",
];

const TELESTRATOR_TOOLS_AS_EDITOR: readonly EditorToolKind[] =
  TELESTRATOR_TOOL_KINDS as readonly EditorToolKind[];

type Surface = "tactical_board" | "video";

const PANEL_ID = "coach-review-floating-tool-panel";

/** The toggle button — rendered in the focus-mode header next to
 * Exit focus. Styled to match the rest of the header chrome
 * (small, theme-token colors). */
export function FloatingToolToggle(props: {
  surface: Surface;
  open: boolean;
  onToggle: () => void;
}) {
  const surfaceKey = props.surface === "tactical_board" ? "tactical" : "telestrator";
  const armedTool = useReviewEditorStore((s) => s[surfaceKey].armedTool);

  return (
    <button
      type="button"
      onClick={props.onToggle}
      aria-expanded={props.open}
      aria-controls={PANEL_ID}
      data-testid="coach-review-floating-tools-toggle"
      className="rounded border border-rule/60 bg-canvas px-2.5 py-1 text-xs font-display uppercase tracking-wide text-ink-muted transition-colors hover:bg-field hover:text-ink"
    >
      {props.open ? "Close tools" : `Tools · ${armedTool}`}
    </button>
  );
}

/** The tool + composer panel — rendered as a fixed-position
 * overlay anchored beneath the focus-mode header. Stays MOUNTED
 * while focus mode is active and only `hidden` when collapsed,
 * so `children` (the live RHF composer) is never destroyed by a
 * "Close tools" click. */
export function FloatingToolPanel(props: {
  surface: Surface;
  open: boolean;
  children?: React.ReactNode;
}) {
  const surfaceKey = props.surface === "tactical_board" ? "tactical" : "telestrator";
  const armedTool = useReviewEditorStore((s) => s[surfaceKey].armedTool);
  const color = useReviewEditorStore((s) => s[surfaceKey].color);
  const strokeWidth = useReviewEditorStore((s) => s[surfaceKey].strokeWidth);
  const armTool = useReviewEditorStore((s) => s.armTool);
  const setColor = useReviewEditorStore((s) => s.setColor);
  const setStrokeWidth = useReviewEditorStore((s) => s.setStrokeWidth);

  const tools =
    props.surface === "tactical_board" ? TACTICAL_TOOLS : TELESTRATOR_TOOLS_AS_EDITOR;

  return (
    <div
      id={PANEL_ID}
      role="group"
      aria-label="Drawing tools and composer"
      data-testid="coach-review-floating-tool-panel"
      className={
        props.open
          ? "fixed inset-x-3 top-[calc(3rem_+_env(safe-area-inset-top))] z-[121] bg-surface/95 backdrop-blur border border-rule rounded p-3 pb-[calc(0.75rem_+_env(safe-area-inset-bottom))] shadow-pop max-h-[calc(100dvh_-_4rem_-_env(safe-area-inset-top))] overflow-y-auto flex flex-col gap-4 sm:left-auto sm:w-96 sm:p-4"
          : "hidden"
      }
    >
      <ToolGrid tools={tools} value={armedTool} onChange={(t) => armTool(t)} />
      <ColorPalette value={color as SharedColor} onChange={(c) => setColor(c)} />
      <StrokeWidthSlider value={strokeWidth} onChange={(w) => setStrokeWidth(w)} />
      {props.children ? (
        <div className="flex flex-col gap-4 pt-4 border-t border-rule/40">
          {props.children}
        </div>
      ) : null}
      <p className="text-xs text-ink-muted">
        Press <kbd className="font-mono">Esc</kbd> to exit focus mode.
      </p>
    </div>
  );
}
